import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { subscribeOutboxEvents, subscribeOutbox } from "@/lib/prescription-outbox";
import {
  loadDesktopNotifPrefs,
  subscribeDesktopNotifPrefs,
  type DesktopNotifPrefs,
} from "@/lib/desktop-notification-prefs";

/**
 * Bridges app events → OS-native desktop notifications and tray badge
 * updates when running inside the DoctorsX Electron desktop app.
 *
 * Fires notifications for:
 *   • Prescription retry (transient failure → will retry with backoff)
 *   • Prescription permanent failure (needs user action)
 *   • New patient inserted (from another tab / device / kiosk)
 *
 * In a browser this component is a no-op — all APIs are guarded on
 * `window.electronRuntime?.isElectron`.
 */

type NotifyPayload = {
  title: string;
  body: string;
  silent?: boolean;
  urgency?: "low" | "normal" | "critical";
  navigate?: string;
};

declare global {
  interface Window {
    electronTray?: {
      setBadgeCount: (n: number) => void;
      setTooltip: (text: string) => void;
      flash: (on: boolean) => void;
    };
    electronNotifier?: { notify: (p: NotifyPayload) => void };
  }
}

function notify(payload: NotifyPayload) {
  if (typeof window === "undefined") return;
  if (window.electronNotifier) {
    window.electronNotifier.notify(payload);
    return;
  }
  // Browser fallback — silently no-op if permission not granted.
  if ("Notification" in window && Notification.permission === "granted") {
    const n = new Notification(payload.title, { body: payload.body, silent: payload.silent });
    n.onclick = () => window.focus();
  }
}

const isDesktop = () =>
  typeof window !== "undefined" && !!window.electronRuntime?.isElectron;

export function DesktopNotifier() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const prefsRef = useRef<DesktopNotifPrefs>(loadDesktopNotifPrefs());

  // Keep latest prefs available to event callbacks without re-subscribing.
  useEffect(() => {
    prefsRef.current = loadDesktopNotifPrefs();
    return subscribeDesktopNotifPrefs((p) => {
      prefsRef.current = p;
      // When the tray badge is disabled, clear it immediately.
      if (!p.trayBadge) window.electronTray?.setBadgeCount(0);
    });
  }, []);

  // Tray badge + outbox lifecycle events → notifications
  useEffect(() => {
    if (!isDesktop()) return;

    const unsubCount = subscribeOutbox((count) => {
      if (!prefsRef.current.trayBadge) return;
      window.electronTray?.setBadgeCount(count);
    });

    const unsubEvents = subscribeOutboxEvents((e) => {
      const prefs = prefsRef.current;
      if (e.type === "retry") {
        if (!prefs.retries) return;
        const mins = e.next_retry_at
          ? Math.max(1, Math.round((e.next_retry_at - Date.now()) / 60000))
          : null;
        notify({
          title: "Prescription retry scheduled",
          body: `Sync failed (attempt ${e.attempts}). ${
            mins ? `Retrying in ~${mins} min.` : "Retrying soon."
          } ${e.error.slice(0, 100)}`,
          urgency: "low",
          navigate: "/prescriptions",
        });
      } else if (e.type === "failure") {
        if (!prefs.failures) return;
        notify({
          title: "Prescription failed to sync",
          body: `Needs your attention${e.code ? ` [${e.code}]` : ""}: ${e.error.slice(0, 140)}`,
          urgency: "critical",
          navigate: "/prescriptions",
        });
        window.electronTray?.flash(true);
      } else if (e.type === "synced") {
        if (!prefs.synced) return;
        notify({
          title: "Prescriptions synced",
          body: `${e.count} offline prescription${e.count === 1 ? "" : "s"} uploaded successfully.`,
          silent: true,
          navigate: "/prescriptions",
        });
      }
    });

    return () => {
      unsubCount();
      unsubEvents();
    };
  }, []);

  // Handle in-app navigation from tray / notification clicks
  useEffect(() => {
    if (!isDesktop()) return;
    const handler = (e: Event) => {
      const path = (e as CustomEvent<string>).detail;
      if (typeof path === "string" && path.startsWith("/")) navigate(path);
    };
    window.addEventListener("app:navigate", handler);
    return () => window.removeEventListener("app:navigate", handler);
  }, [navigate]);

  // Realtime: new patient inserted from anywhere → OS notification
  useEffect(() => {
    if (!isDesktop() || !user) return;

    let ready = false;
    const readyTimer = setTimeout(() => { ready = true; }, 1500);

    const channel = supabase
      .channel(`desktop-notif-${user.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "patients", filter: `user_id=eq.${user.id}` },
        (payload) => {
          if (!ready) return;
          if (!prefsRef.current.newPatients) return;
          const p = payload.new as { id?: string; name?: string; phone?: string };
          notify({
            title: "New patient registered",
            body: `${p.name ?? "Unnamed"}${p.phone ? ` · ${p.phone}` : ""}`,
            navigate: p.id ? `/patients/${p.id}` : "/patients",
          });
        },
      )
      .subscribe();

    return () => {
      clearTimeout(readyTimer);
      supabase.removeChannel(channel);
    };
  }, [user]);

  return null;
}
